import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { FaTasks, FaCheckCircle, FaClock } from "react-icons/fa";
import { FiLoader, FiActivity, FiUser, FiAlertCircle } from "react-icons/fi";
import { toast } from "react-toastify";

// Components
import TopbarNav from "../components/TopbarNav";
import SidebarNav from '../components/MasterSideBar';
import AllActivitiesTable from '../components/AllActivitiesTable';

// API
import { fetchProjects as fetchProjectsApi } from "../api/projectApi"; 

// --- Sub-component: Stat Card ---
const StatCard = ({ title, value, icon, loading, color }) => {
    const colorMap = {
        orange: "text-orange-600 bg-orange-50",
        blue: "text-blue-600 bg-blue-50",
        green: "text-green-600 bg-green-50"
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between transition-all duration-200 hover:shadow-md">
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {title}
                </p>
                {loading ? (
                    <div className="mt-2 h-8 w-16 bg-gray-100 animate-pulse rounded" />
                ) : (
                    <p className="text-3xl font-black text-gray-800 mt-1">
                        {value}
                    </p>
                )}
            </div>
            <div className={`p-4 rounded-xl ${colorMap[color]}`}>
                {React.cloneElement(icon, { className: "text-2xl" })}
            </div>
        </div>
    );
};

const getCurrentUser = () => {
    try {
        const userString = localStorage.getItem('user');
        return userString ? JSON.parse(userString) : null;
    } catch (e) {
        return null;
    }
};

const getCurrentUserId = () => {
    const user = getCurrentUser();
    return user ? (user._id || user.id || user.userId) : null;
};

const AllActivitiesPage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [errorProjects, setErrorProjects] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    
    // Sidebar State
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const fetchProjectsWithProgress = async () => {
        setLoadingProjects(true);
        setErrorProjects(null);
        try {
            const data = await fetchProjectsApi();
            const user = getCurrentUser();
            const userId = getCurrentUserId();
            setCurrentUser(user);
            
            if (!userId) {
                toast.error("User information not found. Please log in again.");
                navigate("/");
                return;
            }
            
            // Filter projects to only show those assigned to the current user
            const filteredProjects = data.filter(project => {
                const managerId = typeof project.assignedProgramManager === 'object' 
                    ? (project.assignedProgramManager?._id || project.assignedProgramManager?.id) 
                    : project.assignedProgramManager;
                
                return String(managerId) === String(userId);
            });
            
            setProjects(filteredProjects);
        } catch (err) {
            setErrorProjects("Failed to load project progress.");
            toast.error("Failed to fetch projects.");
        } finally {
            setLoadingProjects(false);
        }
    };

    useEffect(() => {
        fetchProjectsWithProgress();
    }, []);

    // Calculate stats
    const totalProjectsCount = projects.length;
    const inProgressProjectsCount = projects.filter(p => 
        (p.progress || 0) > 0 && (p.progress || 0) < 100
    ).length;
    const completedProjectsCount = projects.filter(p => 
        (p.progress || 0) === 100
    ).length;

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans">
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); navigate("/"); }}
            />
  
            <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchQuery} />
                
                <main className="p-4 md:p-8 mt-16 lg:mt-20 space-y-6">
                    <div className="max-w-full mx-auto">
                        
                        {/* Page Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Project Activities</h1>
                                <p className="text-gray-500 text-sm">Monitor and update progress across your assigned activity logs.</p>
                            </div>
                            {currentUser && (
                                <div className="flex items-center gap-3 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
                                    <div className="p-2 bg-orange-100 text-orange-600 rounded-xl">
                                        <FiUser size={20} />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase leading-none">Logged In</p>
                                        <p className="text-sm font-bold text-gray-700">{currentUser.firstName} {currentUser.lastName}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Stats Cards Section */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {errorProjects ? (
                                <div className="md:col-span-3 bg-red-50 border border-red-100 rounded-2xl p-8 text-center">
                                    <FiAlertCircle className="mx-auto text-red-400 text-4xl mb-3" />
                                    <p className="text-red-700 font-bold uppercase text-xs tracking-widest">Connection Error</p>
                                    <p className="text-red-600 text-sm mt-1">{errorProjects}</p>
                                    <button 
                                        onClick={fetchProjectsWithProgress}
                                        className="mt-4 px-6 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-100"
                                    >
                                        Retry Sync
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <StatCard title="My Projects" value={totalProjectsCount} icon={<FaTasks />} loading={loadingProjects} color="orange" />
                                    <StatCard title="In Progress" value={inProgressProjectsCount} icon={<FaClock />} loading={loadingProjects} color="blue" />
                                    <StatCard title="Completed" value={completedProjectsCount} icon={<FaCheckCircle />} loading={loadingProjects} color="green" />
                                </>
                            )}
                        </div>                     
                        
                        {/* Activities Table Container */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-50 flex items-center gap-3">
                                <div className="p-2 bg-gray-50 rounded-lg text-gray-400">
                                    <FiActivity size={18} />
                                </div>
                                <h2 className="font-black text-gray-800 uppercase tracking-widest text-sm">Detailed Activity Log</h2>
                            </div>

                            {loadingProjects ? (
                                <div className="py-24 flex flex-col items-center justify-center">
                                    <FiLoader className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Decrypting activity data...</p>
                                </div>
                            ) : (
                                <div className="p-2">
                                    <AllActivitiesTable 
                                        searchQuery={searchQuery} 
                                        projects={projects}
                                        currentUser={currentUser}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AllActivitiesPage;